import { addTicketsToBasket, removeTicketsFromBasket, basket } from '@lib/spektrix'
import SpektrixFetcher from '@lib/spektrix-fetcher'

/**
 * Synchronizes meal deals with ticket changes
 * @param {Object} params
 * @param {string} params.action - 'add' or 'remove'
 * @param {Array} params.ticketsToProcess - Array of ticket objects for add, or ticket IDs for remove
 * @param {Object} params.instanceData - Instance data including meal deals (optional)
 * @param {Object} params.mealDealInfo - Info about meal deals (from instance.meal_deals)
 * @param {Object} params.currentBasket - Current basket data (optional, will fetch if not provided)
 * @returns {Promise} - Resolves when meal deals are synchronized
 */
export async function syncMealDealsWithTickets({ action, ticketsToProcess, instanceData, mealDealInfo, currentBasket }) {
  // Check if this instance has meal deals available
  const availableMealDeals = mealDealInfo?.meal_deals || []
  
  if (availableMealDeals.length === 0) {
    return action === 'add' 
      ? addTicketsToBasket(ticketsToProcess)
      : removeTicketsFromBasket(ticketsToProcess)
  }
  
  // Use provided basket or fetch the current basket
  if (!currentBasket) {
    currentBasket = await basket()
  }
  
  // Find ALL meal deals in the basket by checking instance IDs
  const mealDealInstanceIds = availableMealDeals
    .map(md => md.spektrix_v3_id || md.id)
    .filter(Boolean)
  
  const allMealDealsInBasket = currentBasket?.tickets?.filter(t => {
    const ticketInstanceId = t.instance?.id
    const isMealDeal = mealDealInstanceIds.includes(ticketInstanceId) || 
                      mealDealInstanceIds.includes(String(ticketInstanceId))
    return isMealDeal
  }) || []
  
  if (action === 'add') {
    // Only sync meal deals if the user has already opted in by having meal deals in their basket
    if (allMealDealsInBasket.length === 0) {
      return addTicketsToBasket(ticketsToProcess)
    }
    
    // When adding tickets, also add meal deals to maintain 1:1 ratio
    const ticketsToAdd = [...ticketsToProcess]
    
    // Use the first available meal deal instance
    const mealDealInstance = availableMealDeals[0]
    if (!mealDealInstance) {
      return addTicketsToBasket(ticketsToProcess)
    }
    
    // Get the ticket type from existing meal deals in basket
    const sampleMealDeal = allMealDealsInBasket[0]
    const mealDealTicketType = sampleMealDeal.type?.id || sampleMealDeal.ticketType?.id
    
    if (!mealDealTicketType) {
      console.error('Could not determine meal deal ticket type from existing meal deals')
      return addTicketsToBasket(ticketsToProcess)
    }
    
    // Get the seating plan from the existing meal deal
    const mealDealSeatingPlan = sampleMealDeal.plan?.id || sampleMealDeal.seatingPlan || sampleMealDeal.planId
    
    // For each ticket being added, add a corresponding meal deal
    ticketsToProcess.forEach(() => {
      const mealDealTicket = {
        instance: mealDealInstance.spektrix_v3_id || mealDealInstance.id,
        seatingPlan: mealDealSeatingPlan,
        type: mealDealTicketType
      }
      ticketsToAdd.push(mealDealTicket)
    })
    
    // Add all tickets (main + meal deals) in one call
    return addTicketsToBasket(ticketsToAdd)
    
  } else {
    // When removing tickets, also remove corresponding meal deals
    const idsToRemove = Array.isArray(ticketsToProcess) ? ticketsToProcess : [ticketsToProcess]
    
    // Count how many non-meal-deal tickets will remain after removal
    const remainingMainTickets = currentBasket?.tickets?.filter(t => {
      const ticketInstanceId = t.instance?.id
      const isMealDeal = mealDealInstanceIds.includes(ticketInstanceId) || 
                        mealDealInstanceIds.includes(String(ticketInstanceId))
      // It's a main ticket if it's not a meal deal and not being removed
      return !isMealDeal && !idsToRemove.includes(t.id)
    }).length || 0
    
    if (remainingMainTickets === 0) {
      // No main tickets will remain, so remove ALL meal deals
      const allMealDealIds = allMealDealsInBasket.map(md => md.id)
      return removeTicketsFromBasket([...idsToRemove, ...allMealDealIds])
    } else {
      // Some main tickets will remain, so only remove proportional meal deals
      const mealDealIdsToRemove = allMealDealsInBasket
        .slice(0, idsToRemove.length)
        .map(md => md.id)
      
      // Remove both tickets and meal deals together
      return removeTicketsFromBasket([...idsToRemove, ...mealDealIdsToRemove])
    }
  }
}

/**
 * Gets meal deal template data from instance
 * @param {Object} instance - Instance object
 * @param {Object} basketData - Current basket data
 * @returns {Object|null} - Meal deal template or null if not available
 */
export function getMealDealTemplate(instance, basketData) {
  // Check for meal deals in basket for this instance
  const instanceId = instance.id
  const groupedItems = basketData?.groupedItems || {}
  
  // Find instance data
  const instanceData = Object.values(groupedItems).find(eventData => 
    eventData.instances && eventData.instances[instanceId]
  )?.instances?.[instanceId]
  
  if (!instanceData?.mealDeals || instanceData.mealDeals.length === 0) {
    return null
  }
  
  // Get sample meal deal
  const sampleMealDeal = instanceData.mealDeals[0]
  const mealDealInstance = instance.meal_deal_instances?.[0]
  
  if (!mealDealInstance) {
    return null
  }
  
  return {
    instance: mealDealInstance.spektrix_v3_id || mealDealInstance.id,
    seatingPlan: sampleMealDeal.plan?.id || sampleMealDeal.planId,
    type: sampleMealDeal.type?.id || sampleMealDeal.type
  }
}